import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.tsx';
import type { OAuthScope } from '@runninglime/shared';

const links: { to: string; label: string; icon: string; requiredPermission?: OAuthScope }[] = [
  { to: '/admin', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { to: '/admin/products', label: 'Products', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4', requiredPermission: 'admin:products' },
  { to: '/admin/orders', label: 'Orders', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01', requiredPermission: 'admin:orders' },
  { to: '/admin/tickets', label: 'Tickets', icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z', requiredPermission: 'admin:tickets' },
  { to: '/admin/users', label: 'Users', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', requiredPermission: 'admin:users' },
  { to: '/admin/invites', label: 'Invites', icon: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z', requiredPermission: 'admin:invites' },
  { to: '/admin/enrollment', label: 'Enrollment', icon: 'M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3.5 21.5v-.5a7 7 0 0114 0v.5', requiredPermission: 'admin:enrollment' },
];

// Developer section — admin-only, NOT shown to employees
const developerLinks: { to: string; label: string; icon: string }[] = [
  { to: '/admin/developer', label: 'OAuth Clients', icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' },
];

export default function AdminSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Admins see all links; employees see only permitted ones
  const visibleLinks = links.filter((link) => {
    if (!link.requiredPermission) return true; // Dashboard always visible
    if (user?.role === 'admin') return true;    // Admins see everything
    // Employees: check permissions
    return user?.permissions?.includes(link.requiredPermission);
  });

  return (
    <aside className="w-64 shrink-0 glass-dark sticky top-0 h-screen overflow-y-auto p-4 flex flex-col">
      <div className="mb-6">
        <h2 className="text-lime font-bold text-sm uppercase tracking-wider">Admin Panel</h2>
      </div>
      <nav className="space-y-1">
        {visibleLinks.map((link) => {
          const active = link.to === '/admin'
            ? location.pathname === '/admin'
            : location.pathname.startsWith(link.to);

          return (
            <Link
              key={link.to}
              to={link.to}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                active
                  ? 'bg-lime/20 text-lime border-l-3 border-lime pl-4 shadow-sm shadow-lime/10'
                  : 'text-white/60 hover:text-white hover:bg-white/5 hover:pl-4'
              }`}
            >
              <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d={link.icon} />
              </svg>
              {link.label}
            </Link>
          );
        })}

        {/* Developer Section — admin-only (not for employees) */}
        {user?.role === 'admin' && (
          <>
            <div className="pt-4 pb-1">
              <p className="px-3 text-[10px] font-bold uppercase tracking-widest text-white/30">Developer</p>
            </div>
            {developerLinks.map((link) => {
              const active = location.pathname.startsWith(link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                    active
                      ? 'bg-lime/20 text-lime border-l-3 border-lime pl-4 shadow-sm shadow-lime/10'
                      : 'text-white/60 hover:text-white hover:bg-white/5 hover:pl-4'
                  }`}
                >
                  <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={link.icon} />
                  </svg>
                  {link.label}
                </Link>
              );
            })}
          </>
        )}
      </nav>

      {/* Exit to Home */}
      <div className="mt-auto pt-4 border-t border-white/10">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-all w-full"
        >
          <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3-3h-9m0 0l3-3m-3 3l3 3" />
          </svg>
          Exit to Home
        </button>
      </div>
    </aside>
  );
}
